import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Interface to define a focusable item registered in the system.
 */
export interface FocusableItem {
  element: HTMLElement;
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class TvNavigationService {
  private focusableItems: FocusableItem[] = [];
  private currentFocusIndex = -1;
  public isTvMode = false;
  private isTvModeSubject = new BehaviorSubject<boolean>(false);
  public isTvMode$ = this.isTvModeSubject.asObservable();

  // Key Codes Mapping
  private readonly KEYS = {
    UP: [38],
    DOWN: [40],
    LEFT: [37],
    RIGHT: [39],
    ENTER: [13],
    BACK: [27, 8, 461, 10009], // 461: WebOS Back, 10009: Tizen Back
  };

  constructor(private ngZone: NgZone) {
    this.initListener();
  }

  /**
   * Initialize global keydown listener.
   * We run this outside Angular zone to prevent excessive change detection cycles
   * on every key stroke, re-entering only when necessary.
   */
  private initListener() {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('keydown', (event) => {
        this.handleKeyDown(event);
      });
      
      // Auto-disable TV mode when a mouse is used, restoring the cursor
      window.addEventListener('mousemove', () => {
        if (this.isTvMode) {
          this.disableTvMode();
        }
      });
    });
  }

  /**
   * Main Keyboard Event Handler
   */
  private handleKeyDown(event: KeyboardEvent) {
    const code = event.keyCode;

    // Check if it's a navigational key
    const isNavKey =
      this.KEYS.UP.includes(code) ||
      this.KEYS.DOWN.includes(code) ||
      this.KEYS.LEFT.includes(code) ||
      this.KEYS.RIGHT.includes(code);

    const isEnter = this.KEYS.ENTER.includes(code);
    const isBack = this.KEYS.BACK.includes(code);

    // Only enable TvMode automatically on directional arrow keys, 
    // to prevent Enter or Backspace (typing) from hiding the mouse cursor for desktop users.
    if (isNavKey && !this.isTvMode) {
      this.enableTvMode();
    }

    if (!this.isTvMode) {
      return;
    }

    if (isNavKey) {
      event.preventDefault(); // Prevent scrolling
      this.ngZone.run(() => {
        this.navigate(code);
      });
    } else if (isEnter) {
        // Let default behavior happen (click), but ensure focus logic is aware if needed
        // Sometimes creating a strict 'click' trigger here is safer
        const current = this.getCurrentItem();
        if(current) {
            // alert('Enter pressed on ' + current.element.tagName); 
            // We rely on the directive to handle Enter usually, or native button behavior.
            // But forcing a click here ensures compatibility with divs/spans that share logic.
             this.ngZone.run(() => {
                current.element.click();
             });
        }
    }
  }

  private enableTvMode() {
    this.isTvMode = true;
    this.isTvModeSubject.next(true);
    document.body.classList.add('tv-mode');
    
    // Attempt to focus the first element if nothing is focused
    if (this.currentFocusIndex === -1 && this.focusableItems.length > 0) {
        this.setFocus(0);
    }
  }

  public disableTvMode() {
    this.isTvMode = false;
    this.isTvModeSubject.next(false);
    document.body.classList.remove('tv-mode');
    
    if (this.currentFocusIndex !== -1) {
        const item = this.focusableItems[this.currentFocusIndex];
        if (item) {
           item.element.classList.remove('tv-focused');
        }
        this.currentFocusIndex = -1;
    }
  }

  /**
   * Register a new focusable element
   */
  public register(element: HTMLElement): string {
    const id = Math.random().toString(36).substr(2, 9);
    this.focusableItems.push({ element, id });
    return id;
  }

  /**
   * Unregister an element (e.g. ngOnDestroy)
   */
  public unregister(id: string) {
    const index = this.focusableItems.findIndex((i) => i.id === id);
    if (index !== -1) {
      // If we are removing the currently focused item, reset or move focus
      if (index === this.currentFocusIndex) {
        this.currentFocusIndex = -1;
      } else if (index < this.currentFocusIndex) {
        // Adjust index if an item before the current one is removed
        this.currentFocusIndex--;
      }
      this.focusableItems.splice(index, 1);
    }
  }

  /**
   * Core Spatial Navigation Algorithm
   * Finds the "best" next element based on direction
   */
  private navigate(keyCode: number) {
    if (this.focusableItems.length === 0) return;

    if (this.currentFocusIndex === -1) {
      this.setFocus(0);
      return;
    }

    const currentItem = this.focusableItems[this.currentFocusIndex];
    const currentRect = currentItem.element.getBoundingClientRect();
    const currentCenter = {
      x: currentRect.left + currentRect.width / 2,
      y: currentRect.top + currentRect.height / 2,
    };

    let bestCandidate = -1;
    let minDistance = Number.MAX_VALUE;

    this.focusableItems.forEach((item, index) => {
      if (index === this.currentFocusIndex) return;

      // Ensure element is visible
      const rect = item.element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0 || window.getComputedStyle(item.element).display === 'none') {
          return;
      }
      
      const center = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };

      // Vector from current to candidate
      const dx = center.x - currentCenter.x;
      const dy = center.y - currentCenter.y;

      // Filter based on direction
      let isValidDirection = false;
      // Weights to prefer items primarily in the direction of movement
      // We start with a generic Euclidean distance but penalize orthogonality
      let dist = dx * dx + dy * dy;

      if (this.KEYS.UP.includes(keyCode)) {
        // Target must be above (dy < 0) and reasonably vertical
        isValidDirection = dy < 0 && Math.abs(dx) < Math.abs(dy) * 2.5; 
      } else if (this.KEYS.DOWN.includes(keyCode)) {
        // Target must be below (dy > 0)
        isValidDirection = dy > 0 && Math.abs(dx) < Math.abs(dy) * 2.5;
      } else if (this.KEYS.LEFT.includes(keyCode)) {
        // Target must be left (dx < 0)
        isValidDirection = dx < 0 && Math.abs(dy) < Math.abs(dx) * 2.5;
      } else if (this.KEYS.RIGHT.includes(keyCode)) {
        // Target must be right (dx > 0)
        isValidDirection = dx > 0 && Math.abs(dy) < Math.abs(dx) * 2.5;
      }

      if (isValidDirection) {
        if (dist < minDistance) {
          minDistance = dist;
          bestCandidate = index;
        }
      }
    });

    if (bestCandidate !== -1) {
      this.setFocus(bestCandidate);
    }
  }

  private setFocus(index: number) {
    this.currentFocusIndex = index;
    const item = this.focusableItems[index];

    // Scroll into view safely
    item.element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    
    // Native focus
    item.element.focus({ preventScroll: true });
    
    // Add class for visual styling (optional backup to :focus)
    item.element.classList.add('tv-focused');
    
    // Remove class from others
    this.focusableItems.forEach((i, idx) => {
        if(idx !== index) i.element.classList.remove('tv-focused');
    });
  }

  private getCurrentItem() {
      if (this.currentFocusIndex !== -1 && this.focusableItems[this.currentFocusIndex]) {
          return this.focusableItems[this.currentFocusIndex];
      }
      return null;
  }
}
